/**
 * The per-row result shape, as `GET /jobs/{id}/result` now returns it.
 *
 * Mirrors `xplatform_mapping_service/src/api_service/models/` — `base.py` plus
 * `response/tm/` and `response/oe/`. The old shape was service-agnostic (one
 * `text`, one open `context` dict, one `top_match` whose `metadata` keys were
 * "whatever the plugin indexed"), so every consumer had to know which plugin
 * wrote a row before it could read it. This one splits along the axis that
 * actually differs:
 *
 *  - what EVERY row has — the index, the two orthogonal statuses, whether
 *    candidates / examples exist, the reviewer's pick;
 *  - what a SERVICE has — a `source` (the input as that service describes it)
 *    and a `target` (the catalogue entry it resolved to).
 *
 * `kind` is the discriminator, and it is on the ROW because a row travels
 * alone: the PATCH and add-row routes return one with no envelope around it.
 * It is deliberately NOT on a target — a target is always nested in something
 * that already says what shape it is.
 *
 * Wire types are snake_case; ViewModels are the camelCase shapes the UI reads.
 * Convert at the boundary with the builders in
 * `src/utils/builders/mappingResult.ts`.
 */

import type { JobStatus, DisplayConfig, JobError, JobErrorViewModel } from "@models/job";
import type { MappingStatus, ReviewStatus } from "@models/job";
import type { ScoreBasis } from "@models/search";

/**
 * Which service's `source` / `target` shapes a row carries.
 *
 * A named set rather than an inline `"tm" | "oe"` so every place that branches
 * on it — the row union, the alternatives fetch, the table variant — is
 * provably talking about the same closed vocabulary, and refers to a member
 * rather than retyping a string. Adding a third service means one edit here
 * and a compile error at every site that has to decide.
 */
export const MappingResultKind = {
  Tm: "tm",
  Oe: "oe",
} as const;
export type MappingResultKind =
  (typeof MappingResultKind)[keyof typeof MappingResultKind];

/**
 * The measurements an OE match can report, keyed by what each one measures.
 *
 * Closed to what the backend actually emits (`_SCORE_KEYS` +
 * `_SCORE_METADATA_KEYS` in `oe/rows.py`) rather than an open
 * `Record<string, number>`: a typo in a key should not compile, and a
 * catalogue-miss proposal reports only `LlmConfidence` — the retrieval numbers
 * are absent on it by design, which `Partial` states and an open record hides.
 */
export const OeScoreKey = {
  Similarity: "similarity",
  Distance: "distance",
  LlmConfidence: "llm_confidence",
} as const;
export type OeScoreKey = (typeof OeScoreKey)[keyof typeof OeScoreKey];

/** How sure the select step is about a match it chose. OE only. */
export const ConfidenceBand = {
  High: "high",
  Medium: "medium",
  Low: "low",
} as const;
export type ConfidenceBand =
  (typeof ConfidenceBand)[keyof typeof ConfidenceBand];

// ---------- Wire types (snake_case, server-shaped) ---------------------

/** The technical-manual input, as extracted from the manual. */
export interface TmSourceWire {
  /** The normalised value — the exact string that was embedded. */
  cleaned_text: string | null;
  /** Where in the document it was read from (breadcrumb). */
  path: string | null;
  /** The value exactly as extracted, before cleaning. */
  raw_text: string | null;
  /** Vehicle variant/trim the value was extracted under. */
  variant: string | null;
  /** QA-template category of the pinned field. Not stored on the row —
   *  resolved per request, so it is `null` when the plugin has no template. */
  category: string | null;
  /** maintenance_schedule only: the condition qualifying the interval. */
  remark: string | null;
}

/**
 * One technical-manual catalogue value — the row's match, or a candidate for
 * it. The same shape serves both: `rank` is what tells them apart, and what a
 * pick is made with.
 */
export interface TmTargetWire {
  rank: number | null;
  key: string | null;
  value_display: string | null;
  item_mp_id: number | null;
  item_mp_listing: string | null;
  unit: string | null;
  /** 0-100. `null` when nothing was measured — render nothing rather than a
   *  substitute; `score_basis` says what the number is evidence of. */
  score: number | null;
  /** What `score` is evidence of. The vocabulary is the search service's
   *  (`ScoreBasis`), passed through unchanged. */
  score_basis: ScoreBasis | null;
  /** The value carries the queried number in a compatible unit, decided
   *  arithmetically over the search service's whole pool — not by cosine. */
  exact_value_match: boolean | null;
}

/** The OE input — one original-equipment part as the supplier names it. */
export interface OeSourceWire {
  oe_number: string | null;
  oe_name: string | null;
  oe_function: string | null;
  oe_domain: string | null;
}

/**
 * One catalogue component — the row's match, or a candidate for it.
 *
 * On a `no_item_found` row, rank 1 is the select LLM's PROPOSAL: `itemmp_id`
 * and `itemmp_text` are both `null` and `suggested_text` carries the name the
 * model thinks the missing entry should have. That emptiness IS the finding —
 * there is no catalogue record to name.
 */
export interface OeTargetWire {
  rank: number | null;
  itemmp_id: number | null;
  itemmp_text: string | null;
  /** The measurements behind `score`. A proposal reports only
   *  `llm_confidence` — it was not measured against anything. */
  scores: Partial<Record<OeScoreKey, number>>;
  main_group: string | null;
  sub_group: string | null;
  score: number | null;
  /** An AI suggestion, never a stored record. */
  suggested_text: string | null;
  reason: string | null;
  /** How confident the select step is in THIS match — `high` | `medium` |
   *  `low`. OE only: TM measures nothing finer than "low", which reaches the
   *  client as `mapping_status=needs_review`. `null` on a proposal — no
   *  candidate was chosen, so there is no confidence in one to report.
   *
   *  Kept as its own field rather than folded into `mappingStatus` because
   *  that one is single-valued: a row that is both low-confidence and a
   *  catalogue miss reports the miss, and the confidence would vanish. */
  confidence_band: ConfidenceBand | null;
}

interface MappingResultRowBaseWire {
  row_index: number;
  review_status: ReviewStatus;
  mapping_status: MappingStatus;
  has_examples: boolean;
  /** True when this row has candidates besides the one `target` points at.
   *  A property of the ROW — fetch the candidates from
   *  `GET .../results/{row_index}/alternatives`. */
  has_alternative: boolean;
  error: JobError | null;
  /** The rank the reviewer picked, once they pick one. `null` until then —
   *  `target` is the pipeline's own top, at `target.rank` 1. */
  chosen_rank: number | null;
}

export interface MappingResultRowTmWire extends MappingResultRowBaseWire {
  kind: typeof MappingResultKind.Tm;
  source: TmSourceWire;
  /** `null` when there is nothing to resolve to — see `mapping_status`. */
  target: TmTargetWire | null;
}

export interface MappingResultRowOeWire extends MappingResultRowBaseWire {
  kind: typeof MappingResultKind.Oe;
  source: OeSourceWire;
  target: OeTargetWire | null;
}

export type MappingResultRowWire =
  | MappingResultRowTmWire
  | MappingResultRowOeWire;

export interface MappingResultResponseWire {
  job_id: string;
  status: JobStatus;
  /** Fixes which row shape `rows` carries. */
  plugin_id: string;
  rows: MappingResultRowWire[];
  cursor: string | null;
  display: DisplayConfig | null;
}

/**
 * Body of `GET /jobs/{id}/results/{row_index}/alternatives`.
 *
 * No `kind` here, unlike a row: one request is one row, whose kind the caller
 * already had in hand when it opened the dropdown. The two target shapes have
 * disjoint field names, so they parse unambiguously either way — but the
 * builder takes the kind explicitly rather than sniffing fields.
 */
export interface RowAlternativesResponseWire {
  job_id: string;
  row_index: number;
  /** Rank 1 first — the row's ORIGINAL top match, kept in the list so a
   *  correction stays undoable. */
  alternatives: (TmTargetWire | OeTargetWire)[];
}

// ---------- ViewModel types (camelCase, UI-shaped) ---------------------

export interface TmSource {
  cleanedText: string | null;
  path: string | null;
  rawText: string | null;
  variant: string | null;
  category: string | null;
  remark: string | null;
}

export interface TmTarget {
  rank: number | null;
  key: string | null;
  valueDisplay: string | null;
  itemMpId: number | null;
  itemMpListing: string | null;
  unit: string | null;
  score: number | null;
  scoreBasis: ScoreBasis | null;
  exactValueMatch: boolean | null;
}

export interface OeSource {
  oeNumber: string | null;
  oeName: string | null;
  oeFunction: string | null;
  oeDomain: string | null;
}

export interface OeTarget {
  rank: number | null;
  itemMpId: number | null;
  itemMpText: string | null;
  scores: Partial<Record<OeScoreKey, number>>;
  mainGroup: string | null;
  subGroup: string | null;
  score: number | null;
  suggestedText: string | null;
  reason: string | null;
  /** See `OeTargetWire.confidence_band`. */
  confidenceBand: ConfidenceBand | null;
  /** True when this target is the select LLM's proposal rather than a
   *  catalogue record. Derived, not sent: a proposal is exactly a target with
   *  a `suggestedText` and no `itemMpId`. Branch on THIS, not on the row's
   *  `mappingStatus` — that is the pipeline's verdict and never changes, so a
   *  row corrected onto a real candidate still reads `no_item_found`. */
  isProposal: boolean;
}

interface MappingResultRowBase {
  rowIndex: number;
  reviewStatus: ReviewStatus;
  mappingStatus: MappingStatus;
  hasExamples: boolean;
  hasAlternative: boolean;
  error: JobErrorViewModel | null;
  chosenRank: number | null;
}

export interface MappingResultRowTm extends MappingResultRowBase {
  kind: typeof MappingResultKind.Tm;
  source: TmSource;
  target: TmTarget | null;
}

export interface MappingResultRowOe extends MappingResultRowBase {
  kind: typeof MappingResultKind.Oe;
  source: OeSource;
  target: OeTarget | null;
}

/** Narrow on `kind` before reading `source` / `target`. */
export type MappingResultRow = MappingResultRowTm | MappingResultRowOe;

export interface MappingResult {
  jobId: string;
  status: JobStatus;
  pluginId: string;
  rows: MappingResultRow[];
  cursor: string | null;
  display: DisplayConfig | null;
}

/** Any catalogue entry a row points at — or could point at. */
export type MappingResultTarget = TmTarget | OeTarget;

export type RowAlternatives =
  | {
      kind: typeof MappingResultKind.Tm;
      jobId: string;
      rowIndex: number;
      alternatives: TmTarget[];
    }
  | {
      kind: typeof MappingResultKind.Oe;
      jobId: string;
      rowIndex: number;
      alternatives: OeTarget[];
    };

/**
 * One entry of the candidate dropdown: the target, plus whether it is the one
 * the row currently points at.
 *
 * `isCurrent` is computed once, from `target.rank` against the row's resolved
 * pick — so no component ever compares a rank to `1` again. That comparison is
 * what conflated "the first candidate" with "the row's answer", and it stopped
 * being true the moment a reviewer corrected onto rank 3.
 */
export interface Candidate<T extends MappingResultTarget> {
  target: T;
  isCurrent: boolean;
}

// ---------- Guards -----------------------------------------------------

export const isTmRow = (row: MappingResultRow): row is MappingResultRowTm =>
  row.kind === MappingResultKind.Tm;

export const isOeRow = (row: MappingResultRow): row is MappingResultRowOe =>
  row.kind === MappingResultKind.Oe;
